// @flow

import * as React from "react";
import { useSelector, useDispatch } from "react-redux";
import { ReactForm, ReactFormField } from "@adrenalin/react-form";
// import { ReactForm, ReactFormField } from "../../../../../../react-form";

import Config from "../../constants/Config";
import AuStates from "../../constants/AuStates";
import {
    selectShippingMethod,
    updateCustomerCartDetails,
    resetCheckOutStep,
    setuseDifferentBillingDetails,
    getPaymentOptions,
    checkoutOrder,
    checkoutSuccess,
} from "../../actions/cart";
import useAsyncState from "../../hooks/useAsyncState";
import formatCurrency from "../../utils/formatCurrency";
import { customValidators } from "../../utils/validators";
import Button from "../atoms/Button";
import PayPalButton from "../atoms/PayPalButton";
import EwaySecureFields from "../atoms/EwaySecureFields";
import Icon from "../atoms/Icon";
import ServerErrorMessage from "../atoms/ServerErrorMessage";

import { ReactComponent as PaypalSvg } from "../../../assets/images/paypal.svg";
import { ReactComponent as VisaSvg } from "../../../assets/images/visa.svg";
import { ReactComponent as MastercardSvg } from "../../../assets/images/mastercard.svg";

type PropsType = {
    className?: string,
    successRedirectUrl?: string,
};
const CheckoutSteps = (props: PropsType): React.Node => {
    const dispatch = useDispatch();
    const [state, localActions] = useAsyncState();
    const pickupAddress = useSelector((state: any): any => {
        return state.options.store_pickup_address;
    });
    const [checkoutSteps, setCheckoutSteps] = React.useState([]);
    const cart = useSelector((state: any): any => state.cart);
    const { profile } = useSelector((state: any): any => state.user);

    const selectedShippingMethod =
        cart.shipping_rates[0] && cart.shipping_rates[0].shipping_rates
            ? cart.shipping_rates[0].shipping_rates.find(rate => rate.selected)
            : null;
    const isPickup =
        selectedShippingMethod &&
        selectedShippingMethod.method_id === "local_pickup"
            ? true
            : false;
    const isQuickRegistration =
        cart.clubQuickRegistration && cart.clubQuickRegistration.clubId;
    const needsShipping = cart.needs_shipping && cart.shipping_rates[0];

    const cssBaseClass = "checkout-steps";
    const classList = [cssBaseClass];
    if (props.className) {
        classList.push(props.className);
    }

    const handleShippingMethodSubmit = (formData, updateCheckoutStep) => {
        localActions.request();

        dispatch(
            selectShippingMethod(
                Number(formData.package_id),
                formData.rate_id,
                updateCheckoutStep
            )
        )
            .then(res => {
                localActions.success();
            })
            .catch((error: any) => {
                console.error(error);
                localActions.fail(error.message);
            });
    };

    const handleYourDetailsSubmit = formData => {
        const formDataWithDefaults = {
            "billing_address-country": "AU",
            "billing_address-address_1": cart.billing_address.address_1,
            "billing_address-address_2": cart.billing_address.address_2,
            "billing_address-city": cart.billing_address.city,
            "billing_address-state": cart.billing_address.state,
            "billing_address-postcode": cart.billing_address.postcode || "0000",
            ...formData,
        };

        // prep billing/shipping fields for update customer payload object
        const customerDetails = {
            billing_address: { country: "AU" },
            shipping_address: { country: "AU" },
        };
        Object.keys(formDataWithDefaults).forEach(formKey => {
            const addressType = formKey.split("-")[0];
            const fieldKey = formKey.split("-")[1];
            const useFillerValue = ["address_1", "city"].includes(fieldKey);

            if (!formDataWithDefaults.use_differnt_billing) {
                customerDetails.billing_address[fieldKey] =
                    !formDataWithDefaults[formKey] && useFillerValue
                        ? Config.wcEmptyField
                        : formDataWithDefaults[formKey];

                customerDetails.shipping_address[fieldKey] =
                    !formDataWithDefaults[formKey] && useFillerValue
                        ? Config.wcEmptyField
                        : formDataWithDefaults[formKey];

                // customerDetails.billing_address[fieldKey] =
                //     formDataWithDefaults[formKey] || Config.wcEmptyField;

                // customerDetails.shipping_address[fieldKey] =
                //     formDataWithDefaults[formKey] || Config.wcEmptyField;
            } else {
                if (customerDetails[addressType]) {
                    customerDetails[addressType][fieldKey] =
                        !formDataWithDefaults[formKey] && useFillerValue
                            ? Config.wcEmptyField
                            : formDataWithDefaults[formKey];
                }
            }
        });

        localActions.request();
        Promise.all([
            dispatch(updateCustomerCartDetails(customerDetails)), // save billing/shipping details in cart state
            dispatch(getPaymentOptions()), // save billing/shipping details in cart state
        ])
            .then(res => {
                if (!cart.needs_payment) {
                    // Free cart, submit the order to the checkout without payment
                    dispatch(checkoutOrder("eway"))
                        .then(checkoutSuccessResponse => {
                            handleCheckoutSuccess(checkoutSuccessResponse);
                        })
                        .catch(error => {
                            localActions.fail(
                                error.message ||
                                    "There was an issue processing your transaction."
                            );
                            console.error(error);
                        });
                } else {
                    localActions.success();
                }
            })
            .catch((error: any) => {
                console.error(error);
                localActions.fail(error.message);
            });
    };

    const handleUseDifferentBillingClick = e => {
        dispatch(setuseDifferentBillingDetails(e.currentTarget.checked));
    };

    const handleCheckoutSuccess = (orderSuccessObject: {}) => {
        localActions.request();
        dispatch(checkoutSuccess(orderSuccessObject, props.successRedirectUrl));
    };

    const renderShippingMethodLabel = shippingMethod => {
        const isPickup =
            shippingMethod && shippingMethod.method_id === "local_pickup"
                ? true
                : false;
        let deliveryFee = "Free shipping, 5-7 business days";

        if (shippingMethod.price > 0) {
            deliveryFee = `Flat rate ${formatCurrency(
                Number(shippingMethod.price) + Number(shippingMethod.taxes)
            )}, 5-7 business days`;
        }
        return (
            <>
                <strong>{isPickup ? "Pick up from Store" : "Delivery"}</strong>
                <div>{isPickup ?  pickupAddress  : deliveryFee}</div>
            </>
        );
    };

    const returnEmptyWCFieldValue = value => {
        return value === Config.wcEmptyField ? "" : value;
    };

    const setupSteps = () => {
        const steps = [];

        // Shipping selection
        if (needsShipping) {
            steps.push({
                active: !cart.shippingStepComplete,
                complete: cart.shippingStepComplete,
                title: <>Shipping method</>,
                onEditClick: () => {
                    dispatch(resetCheckOutStep("shippingStepComplete"));
                    dispatch(resetCheckOutStep("detailsStepComplete"));
                },
                completeContent: (
                    <>
                        {cart.shipping_rates[0].shipping_rates.map(
                            (shippingMethod, index) => {
                                if (shippingMethod.selected) {
                                    return (
                                        <div key={index}>
                                            {renderShippingMethodLabel(
                                                shippingMethod
                                            )}
                                        </div>
                                    );
                                } else {
                                    return null;
                                }
                            }
                        )}
                    </>
                ),
                editContent: (
                    <>
                        <ReactForm
                            onSuccess={formData => {
                                handleShippingMethodSubmit(formData, true);
                            }}
                            onUpdate={formData => {
                                handleShippingMethodSubmit(formData);
                            }}
                            validators={customValidators}
                        >
                            {cart.shipping_rates[0].shipping_rates.map(
                                shippingMethod => {
                                    return (
                                        <ReactFormField
                                            key={shippingMethod.rate_id}
                                            type="radio"
                                            label={renderShippingMethodLabel(
                                                shippingMethod
                                            )}
                                            name={"rate_id"}
                                            value={shippingMethod.rate_id}
                                            defaultChecked={
                                                shippingMethod.selected
                                            }
                                            disabled={cart.loading}
                                        />
                                    );
                                }
                            )}
                            <ReactFormField
                                type="text"
                                value={cart.shipping_rates[0].package_id}
                                name={"package_id"}
                                className="hidden"
                                label="package_id"
                            />

                            <Button
                                type="submit"
                                styles={["green"]}
                                loading={state.loading}
                                success={!!state.success}
                            >
                                Continue
                            </Button>
                        </ReactForm>
                    </>
                ),
            });
        }

        // Billing/Shipping Details
        steps.push({
            active: needsShipping
                ? cart.shippingStepComplete && !cart.detailsStepComplete
                : !cart.needs_payment
                ? true
                : !cart.detailsStepComplete,
            complete: cart.detailsStepComplete && cart.needs_payment,
            title: <>Your details</>,
            onEditClick: () =>
                dispatch(resetCheckOutStep("detailsStepComplete")),
            completeContent: (
                <>
                    {needsShipping && !isPickup ? (
                        <>
                            <strong>Delivery</strong>
                            <div>
                                {cart.shipping_address.first_name}{" "}
                                {cart.shipping_address.last_name}
                            </div>
                            <div>{cart.shipping_address.email}</div>
                            <div>
                                {returnEmptyWCFieldValue(
                                    cart.shipping_address.phone
                                )}
                            </div>
                            <div>
                                {returnEmptyWCFieldValue(
                                    cart.shipping_address.address_1
                                )}
                                {returnEmptyWCFieldValue(
                                    cart.shipping_address.address_1
                                ) &&
                                returnEmptyWCFieldValue(
                                    cart.shipping_address.address_2
                                )
                                    ? " "
                                    : null}
                                {returnEmptyWCFieldValue(
                                    cart.shipping_address.address_2
                                )}
                                {returnEmptyWCFieldValue(
                                    cart.shipping_address.address_1
                                ) ||
                                returnEmptyWCFieldValue(
                                    cart.shipping_address.address_2
                                )
                                    ? ", "
                                    : null}
                                {returnEmptyWCFieldValue(
                                    cart.shipping_address.city
                                )}{" "}
                                {returnEmptyWCFieldValue(
                                    cart.shipping_address.state
                                )}{" "}
                                {returnEmptyWCFieldValue(
                                    cart.shipping_address.postcode
                                )}
                            </div>
                            <br />
                        </>
                    ) : null}

                    <strong>Billing</strong>
                    <div>
                        {cart.billing_address.first_name}{" "}
                        {cart.billing_address.last_name}
                    </div>
                    <div>{cart.billing_address.email}</div>
                    <div>
                        {returnEmptyWCFieldValue(cart.billing_address.phone)}
                    </div>
                    {needsShipping && !isPickup ? (
                        <div>
                            {returnEmptyWCFieldValue(
                                cart.billing_address.address_1
                            )}
                            {returnEmptyWCFieldValue(
                                cart.billing_address.address_1
                            ) &&
                            returnEmptyWCFieldValue(
                                cart.billing_address.address_2
                            )
                                ? " "
                                : null}
                            {returnEmptyWCFieldValue(
                                cart.billing_address.address_2
                            )}
                            {returnEmptyWCFieldValue(
                                cart.billing_address.address_1
                            ) ||
                            returnEmptyWCFieldValue(
                                cart.billing_address.address_2
                            )
                                ? ", "
                                : null}
                            {returnEmptyWCFieldValue(cart.billing_address.city)}{" "}
                            {returnEmptyWCFieldValue(
                                cart.billing_address.state
                            )}{" "}
                            {returnEmptyWCFieldValue(
                                cart.billing_address.postcode
                            )}
                        </div>
                    ) : null}
                </>
            ),
            editContent: (
                <>
                    {needsShipping && !isPickup ? (
                        // Shipping and billing form
                        <ReactForm
                            onSuccess={handleYourDetailsSubmit}
                            disableSubmitUntilValid
                            validators={customValidators}
                        >
                            <h4 className="heading-4">Delivery Address</h4>

                            <div className="checkout-steps__form-row row row--padding-small">
                                <div className="col-xs-12 col-md-6">
                                    <ReactFormField
                                        type="text"
                                        label="First name"
                                        name="shipping_address-first_name"
                                        validators={["customname"]}
                                        required={true}
                                        defaultValue={
                                            cart.shipping_address.first_name ||
                                            profile.first_name
                                        }
                                    />
                                </div>
                                <div className="col-xs-12 col-md-6">
                                    <ReactFormField
                                        type="text"
                                        label="Last name"
                                        name="shipping_address-last_name"
                                        validators={["customname"]}
                                        required={true}
                                        defaultValue={
                                            cart.shipping_address.last_name ||
                                            profile.last_name
                                        }
                                    />
                                </div>

                                <div className="col-xs-12">
                                    <ReactFormField
                                        type="email"
                                        label="Email"
                                        name="shipping_address-email"
                                        validators={["email"]}
                                        required={true}
                                        defaultValue={
                                            cart.billing_address.email ||
                                            profile.email
                                        }
                                    />

                                    <ReactFormField
                                        type="text"
                                        label="Mobile number"
                                        name="shipping_address-phone"
                                        validators={["phonenumber"]}
                                        required={true}
                                        defaultValue={
                                            cart.billing_address.phone ||
                                            profile.mobile
                                        }
                                    />

                                    <ReactFormField
                                        type="select"
                                        label="Country"
                                        name="shipping_address-country"
                                    >
                                        <option value="AU">Australia</option>
                                    </ReactFormField>

                                    <ReactFormField
                                        type="text"
                                        label="Address line 1"
                                        name="shipping_address-address_1"
                                        required={true}
                                        defaultValue={returnEmptyWCFieldValue(
                                            cart.shipping_address.address_1
                                        )}
                                    />

                                    <ReactFormField
                                        type="text"
                                        label="Address line 2 (Optional)"
                                        name="shipping_address-address_2"
                                        defaultValue={returnEmptyWCFieldValue(
                                            cart.shipping_address.address_2
                                        )}
                                    />
                                </div>

                                <div className="col-xs-12 col-md-6">
                                    <ReactFormField
                                        type="text"
                                        label="City"
                                        name="shipping_address-city"
                                        required={true}
                                        defaultValue={returnEmptyWCFieldValue(
                                            cart.shipping_address.city
                                        )}
                                    />
                                </div>

                                <div className="col-xs-12 col-md-3">
                                    <ReactFormField
                                        type="select"
                                        label="State"
                                        name="shipping_address-state"
                                        defaultValue={
                                            cart.shipping_address.state
                                        }
                                    >
                                        <option></option>
                                        {AuStates.map(state => {
                                            return (
                                                <option
                                                    key={state.code}
                                                    value={state.code}
                                                >
                                                    {state.code}
                                                </option>
                                            );
                                        })}
                                    </ReactFormField>
                                </div>

                                <div className="col-xs-12 col-md-3">
                                    <ReactFormField
                                        type="text"
                                        label="Postcode"
                                        name="shipping_address-postcode"
                                        required={true}
                                        defaultValue={returnEmptyWCFieldValue(
                                            cart.shipping_address.postcode
                                        )}
                                    />
                                </div>

                                <div className="col-xs-12">
                                    <ReactFormField
                                        type="checkbox"
                                        label="Use different billing address"
                                        name="use_differnt_billing"
                                        onChange={
                                            handleUseDifferentBillingClick
                                        }
                                        defaultChecked={
                                            cart.useDifferentBillingDetails
                                        }
                                    />

                                    {cart.useDifferentBillingDetails ? (
                                        <>
                                            <h4 className="heading-4">
                                                Billing Address
                                            </h4>

                                            <div className="checkout-steps__form-row row row--padding-small">
                                                <div className="col-xs-12 col-md-6">
                                                    <ReactFormField
                                                        type="text"
                                                        label="First name"
                                                        name="billing_address-first_name"
                                                        validators={[
                                                            "customname",
                                                        ]}
                                                        required={true}
                                                        defaultValue={
                                                            cart.billing_address
                                                                .first_name ||
                                                            profile.first_name
                                                        }
                                                    />
                                                </div>
                                                <div className="col-xs-12 col-md-6">
                                                    <ReactFormField
                                                        type="text"
                                                        label="Last name"
                                                        name="billing_address-last_name"
                                                        validators={[
                                                            "customname",
                                                        ]}
                                                        required={true}
                                                        defaultValue={
                                                            cart.billing_address
                                                                .last_name ||
                                                            profile.last_name
                                                        }
                                                    />
                                                </div>

                                                <div className="col-xs-12">
                                                    <ReactFormField
                                                        type="email"
                                                        label="Email"
                                                        name="billing_address-email"
                                                        validators={["email"]}
                                                        required={true}
                                                        className={
                                                            isQuickRegistration ||
                                                            profile.email
                                                                ? "is-readonly"
                                                                : ""
                                                        }
                                                        readOnly={
                                                            isQuickRegistration ||
                                                            profile.email
                                                        }
                                                        defaultValue={
                                                            cart
                                                                .clubQuickRegistration
                                                                .email ||
                                                            profile.email ||
                                                            cart.billing_address
                                                                .email
                                                        }
                                                    />

                                                    <ReactFormField
                                                        type="text"
                                                        label="Mobile number"
                                                        name="billing_address-phone"
                                                        validators={[
                                                            "phonenumber",
                                                        ]}
                                                        required={true}
                                                        defaultValue={
                                                            cart.billing_address
                                                                .phone ||
                                                            profile.mobile
                                                        }
                                                    />

                                                    <ReactFormField
                                                        type="select"
                                                        label="Country"
                                                        name="billing_address-country"
                                                    >
                                                        <option value="AU">
                                                            Australia
                                                        </option>
                                                    </ReactFormField>

                                                    <ReactFormField
                                                        type="text"
                                                        label="Address line 1"
                                                        name="billing_address-address_1"
                                                        required={true}
                                                        defaultValue={returnEmptyWCFieldValue(
                                                            cart.billing_address
                                                                .address_1
                                                        )}
                                                    />

                                                    <ReactFormField
                                                        type="text"
                                                        label="Address line 2 (Optional)"
                                                        name="billing_address-address_2"
                                                        defaultValue={returnEmptyWCFieldValue(
                                                            cart.billing_address
                                                                .address_2
                                                        )}
                                                    />

                                                    <ReactFormField
                                                        type="text"
                                                        label="City"
                                                        name="billing_address-city"
                                                        required={true}
                                                        defaultValue={returnEmptyWCFieldValue(
                                                            cart.billing_address
                                                                .city
                                                        )}
                                                    />

                                                    <ReactFormField
                                                        type="select"
                                                        label="State"
                                                        name="billing_address-state"
                                                        defaultValue={
                                                            cart.billing_address
                                                                .state
                                                        }
                                                    >
                                                        <option></option>
                                                        {AuStates.map(
                                                            (state, index) => {
                                                                return (
                                                                    <option
                                                                        key={
                                                                            index
                                                                        }
                                                                        value={
                                                                            state.code
                                                                        }
                                                                    >
                                                                        {
                                                                            state.code
                                                                        }
                                                                    </option>
                                                                );
                                                            }
                                                        )}
                                                    </ReactFormField>

                                                    <ReactFormField
                                                        type="text"
                                                        label="Postcode"
                                                        name="billing_address-postcode"
                                                        required={true}
                                                        defaultValue={returnEmptyWCFieldValue(
                                                            cart.billing_address
                                                                .postcode
                                                        )}
                                                    />
                                                </div>
                                            </div>
                                        </>
                                    ) : null}

                                    <Button
                                        type="submit"
                                        styles={["green"]}
                                        loading={state.loading}
                                        success={!!state.success}
                                    >
                                        {cart.needs_payment
                                            ? "Continue"
                                            : "Finish"}
                                    </Button>
                                </div>
                            </div>
                        </ReactForm>
                    ) : (
                        // Billing only form

                        <ReactForm
                            onSuccess={handleYourDetailsSubmit}
                            disableSubmitUntilValid
                            validators={customValidators}
                        >
                            <div className="checkout-steps__form-row row row--padding-small">
                                <div className="col-xs-12 col-md-6">
                                    <ReactFormField
                                        type="text"
                                        label="First name"
                                        name="billing_address-first_name"
                                        validators={["customname"]}
                                        required={true}
                                        defaultValue={
                                            cart.clubQuickRegistration
                                                .first_name ||
                                            cart.billing_address.first_name ||
                                            profile.first_name
                                        }
                                    />
                                </div>
                                <div className="col-xs-12 col-md-6">
                                    <ReactFormField
                                        type="text"
                                        label="Last name"
                                        name="billing_address-last_name"
                                        validators={["customname"]}
                                        required={true}
                                        defaultValue={
                                            cart.clubQuickRegistration
                                                .last_name ||
                                            cart.billing_address.last_name ||
                                            profile.last_name
                                        }
                                    />
                                </div>

                                <div className="col-xs-12">
                                    <ReactFormField
                                        type="email"
                                        label="Email"
                                        name="billing_address-email"
                                        validators={["email"]}
                                        required={true}
                                        className={
                                            isQuickRegistration || profile.email
                                                ? "is-readonly"
                                                : ""
                                        }
                                        readOnly={
                                            isQuickRegistration || profile.email
                                        }
                                        defaultValue={
                                            cart.clubQuickRegistration.email ||
                                            profile.email ||
                                            cart.billing_address.email
                                        }
                                    />

                                    <ReactFormField
                                        type="text"
                                        label="Mobile number"
                                        name="billing_address-phone"
                                        validators={["phonenumber"]}
                                        required={true}
                                        defaultValue={
                                            cart.clubQuickRegistration.mobile ||
                                            cart.billing_address.phone ||
                                            profile.mobile
                                        }
                                    />

                                    <Button
                                        type="submit"
                                        styles={["green"]}
                                        loading={state.loading}
                                        success={!!state.success}
                                    >
                                        {cart.needs_payment
                                            ? "Continue"
                                            : "Finish"}
                                    </Button>
                                </div>
                            </div>
                        </ReactForm>
                    )}
                </>
            ),
        });

        // Payment Details
        if (cart.needs_payment) {
            // Check email length when rendering payment options
            const isEmailTooLongForEway = (cart.billing_address?.email || "").length > 50;
            steps.push({
                active: needsShipping
                    ? cart.shippingStepComplete && cart.detailsStepComplete
                    : cart.detailsStepComplete,
                complete: cart.paymentStepComplete,
                title: "Payment",
                editContent: (
                    <>
                        <ReactForm>
                            {cart.paymentOptions
                                ? cart.paymentOptions
                                      .filter(paymentOption => {
                                          if (paymentOption.id === "eway" && isEmailTooLongForEway) {
                                              return false;
                                          }
                                          return true;
                                      })
                                      .map(paymentOption => {
                                          if (paymentOption.id === "eway") {
                                              // Credit card
                                              return (
                                                  <ReactFormField
                                                      key={paymentOption.id}
                                                      className="react-form__field--inset react-form__field--active-info"
                                                      type="radio"
                                                      label={
                                                          <>
                                                              Credit card{" "}
                                                              <VisaSvg />{" "}
                                                              <MastercardSvg />{" "}
                                                          </>
                                                      }
                                                      name={"paymentMethod"}
                                                      value={paymentOption.id}
                                                      activeLabel={
                                                          <div className="react-form__field__active-content">
                                                              <p>
                                                                  We accept VISA,
                                                                  Mastercard
                                                              </p>

                                                              <EwaySecureFields
                                                                  payAmount={
                                                                      cart.totals
                                                                          .total_price
                                                                  }
                                                                  onSuccess={
                                                                      handleCheckoutSuccess
                                                                  }
                                                              />
                                                          </div>
                                                      }
                                                  />
                                              );
                                          } else if (
                                              paymentOption.id === "ppcp-gateway"
                                          ) {
                                              // PayPal
                                              return (
                                                  <ReactFormField
                                                      key={paymentOption.id}
                                                      className="react-form__field--inset"
                                                      type="radio"
                                                      name={"paymentMethod"}
                                                      value={paymentOption.id}
                                                      label={
                                                          <>
                                                              PayPal <PaypalSvg />
                                                          </>
                                                      }
                                                      activeLabel={
                                                          <div className="react-form__field__active-content">
                                                              <p>
                                                                  You will be
                                                                  redirected to
                                                                  Paypal to finalise
                                                                  the payment.
                                                              </p>

                                                              <PayPalButton
                                                                  onSuccess={
                                                                      handleCheckoutSuccess
                                                                  }
                                                              />
                                                          </div>
                                                      }
                                                  />
                                              );
                                          } else {
                                              return null;
                                          }
                                      })
                                : null}
                        </ReactForm>
                    </>
                ),
            });
        }

        setCheckoutSteps(steps);
    };

    React.useEffect(() => {
        setupSteps();
    }, [cart, state]); // eslint-disable-line

    return (
        <div className={classList.join(" ")}>
            <ServerErrorMessage>{state.error}</ServerErrorMessage>

            {checkoutSteps.map((checkoutStep: any, index: number) => {
                return (
                    <CheckoutStepsItem
                        key={index}
                        number={index + 1}
                        totalSteps={checkoutSteps.length}
                        {...checkoutStep}
                    />
                );
            })}
        </div>
    );
};

const CheckoutStepsItem = (props: {
    className?: string,
    number: number,
    totalSteps: number,
    title: string,
    active: boolean,
    complete: boolean,
    completeContent: any,
    editContent: any,
    onEditClick: Function,
}) => {
    const cssBaseClass = "checkout-steps__item";
    const classList = [cssBaseClass];

    if (props.className) {
        classList.push(props.className);
    }

    if (props.complete) {
        classList.push("is-complete");
    }

    if (props.totalSteps < 2) {
        classList.push("is-single");
    }

    if (props.active) {
        classList.push("is-active");
    }

    return (
        <div className={classList.join(" ")}>
            <div className="checkout-steps__item__indicator">
                {props.totalSteps > 1 ? (
                    <>{props.complete ? <Icon name="tick" /> : props.number}</>
                ) : null}
            </div>
            <div className="checkout-steps__item__content">
                <div className="checkout-steps__item__header">
                    <div className="checkout-steps__item__header__title">
                        {props.title}
                    </div>

                    {props.complete ? (
                        <button
                            type="button"
                            className="checkout-steps__item__header__edit"
                            onClick={props.onEditClick}
                        >
                            Edit
                        </button>
                    ) : null}
                </div>

                {props.complete ? (
                    <div className="checkout-steps__item__content__inner">
                        {props.completeContent}
                    </div>
                ) : props.active ? (
                    <div className="checkout-steps__item__content__inner">
                        {props.editContent}
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default CheckoutSteps;
